import { Comment as AntdComment } from '@ant-design/compatible';
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  LikeFilled,
  LikeOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import {
  ApolloCache,
  NormalizedCacheObject,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  CommentType,
  DELETE_DISCUSSION_COMMENT,
  DELETE_DISCUSSION_REPLY,
  Discussion,
  GET_DISCUSSION,
  GET_ME,
  LIKE_DISCUSSION_WALL_COMMENT,
  LIKE_DISCUSSION_WALL_COMMENT_REPLY,
  Me,
  PATCH_DISCUSSION_WALL_COMMENT,
  PATCH_DISCUSSION_WALL_COMMENT_REPLY,
  Reply,
} from '@frontend/data-access';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { UserAvatar } from '@frontend/shared/ui';
import { Button, Input, Spin, Tooltip, Typography } from 'antd';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useRef } from 'react';

import { Thread } from '../discussion-wall/discussion-wall';

const { TextArea } = Input;
const { Text } = Typography;

/* eslint-disable-next-line */
export interface CommentProps {
  discussionId: string;
  comment: CommentType;
  currentUser?: Me;
  showReplies?: boolean;
  hideActions?: boolean;
  currentThread?: Thread;
  setCurrentThread: (thread: Thread | undefined) => void;
}

export function Comment({
  discussionId,
  comment,
  showReplies = true,
  hideActions = false,
  currentUser,
  currentThread,
  setCurrentThread,
}: CommentProps) {
  const { t } = useTranslation('common');
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const prevThread = useRef<Thread | undefined>();
  const [likeComment] = useMutation(LIKE_DISCUSSION_WALL_COMMENT);
  const [likeReply] = useMutation(LIKE_DISCUSSION_WALL_COMMENT_REPLY);
  const [patchComment, { loading: patchDiscussionWallCommentLoading }] =
    useMutation(PATCH_DISCUSSION_WALL_COMMENT, {
      onCompleted() {
        setCurrentThread(undefined);
      },
    });
  const [patchReply, { loading: patchDiscussionWallCommentReplyLoading }] =
    useMutation(PATCH_DISCUSSION_WALL_COMMENT_REPLY, {
      onCompleted() {
        setCurrentThread(undefined);
      },
    });
  const [deleteComment, { loading: deleteCommentLoading }] = useMutation(
    DELETE_DISCUSSION_COMMENT,
    {
      onCompleted() {
        setCurrentThread(undefined);
      },
      update: (cache: ApolloCache<NormalizedCacheObject>) => {
        const cacheData: {
          discussion: Discussion;
        } | null = cache.readQuery({
          query: GET_DISCUSSION,
          variables: {
            id: discussionId,
          },
        });
        if (cacheData) {
          const { discussion } = cacheData;

          if (discussion) {
            cache.writeQuery({
              query: GET_DISCUSSION,
              variables: {
                id: discussionId,
              },
              data: {
                discussion: {
                  ...discussion,
                  comments: {
                    ...discussion?.comments,
                    edges: discussion?.comments.edges?.filter(
                      (c) => c.node.id !== prevThread.current?.id
                    ),
                  },
                },
              },
            });
          }
        }
      },
    }
  );
  const [deleteReply, { loading: deleteReplyLoading }] = useMutation(
    DELETE_DISCUSSION_REPLY,
    {
      onCompleted() {
        setCurrentThread(undefined);
      },
    }
  );

  useEffect(() => {
    if (currentThread) {
      prevThread.current = currentThread;
    }
  }, [currentThread]);

  const renderAuthor = (comment: CommentType | Reply) => {
    return (
      <div className="w-full flex justify-between items-center">
        <Text className="flex-auto pr-2 line-clamp-1">
          {comment.user?.fullName}{' '}
        </Text>
        {comment.updatedAt ? (
          <span className="flex-1">({t('comment_edited')})</span>
        ) : (
          ''
        )}
        {!hideActions && currentUser?.id === comment.user?.id && (
          <Tooltip key="edit-comment" title={t('edit')}>
            <div
              className="ml-2 cursor-pointer"
              onClick={() => {
                setCurrentThread({
                  id: comment.id,
                  text: comment.text,
                  author: comment.user,
                  isEdit: true,
                });
              }}
            >
              <EditOutlined />
            </div>
          </Tooltip>
        )}
        {!hideActions && currentUser?.role === 'ADMIN' && (
          <Tooltip key="delete-reply" title={t('delete')}>
            {(currentThread?.id === comment?.id && deleteReplyLoading) ||
            deleteCommentLoading ? (
              <Spin
                size="small"
                indicator={
                  <span className="text-gray-800 text-[8px]">
                    <LoadingOutlined />
                  </span>
                }
              />
            ) : (
              <div
                className="ml-2 cursor-pointer"
                onClick={() => {
                  setCurrentThread({
                    id: comment?.id,
                    text: comment?.text,
                    author: comment.user,
                    isDelete: true,
                  });
                  comment.__typename === 'Comment'
                    ? deleteComment({
                        variables: {
                          id: comment?.id,
                        },
                      })
                    : deleteReply({
                        variables: {
                          id: comment?.id,
                        },
                      });
                }}
              >
                <DeleteOutlined />
              </div>
            )}
          </Tooltip>
        )}
      </div>
    );
  };

  const renderContentComment = (comment: CommentType) => {
    return currentThread?.isEdit && currentThread.id === comment.id ? (
      <div className="space-y-2">
        <div className="flex justify-end">
          <CloseOutlined onClick={() => setCurrentThread(undefined)} />
        </div>
        <TextArea
          value={currentThread.text}
          autoFocus
          rows={3}
          placeholder={t('add_comment_placeholder')}
          onChange={(e) =>
            setCurrentThread({
              ...currentThread,
              text: e.target.value,
            })
          }
        />
        <Button
          type="primary"
          shape="round"
          loading={patchDiscussionWallCommentLoading}
          onClick={() =>
            patchComment({
              variables: {
                input: {
                  commentId: currentThread.id,
                  text: currentThread.text,
                },
              },
            })
          }
        >
          {t('submit')}
        </Button>
      </div>
    ) : (
      <p>{comment.text}</p>
    );
  };

  const renderContentReply = (reply: Reply) => {
    return currentThread?.isEdit && currentThread.id === reply.id ? (
      <div className="space-y-2 !bg-transparent">
        <div className="flex justify-end">
          <CloseOutlined onClick={() => setCurrentThread(undefined)} />
        </div>
        <TextArea
          value={currentThread.text}
          autoFocus
          rows={3}
          placeholder={t('add_comment_placeholder')}
          onChange={(e) =>
            setCurrentThread({
              ...currentThread,
              text: e.target.value,
            })
          }
        />

        <Button
          type="primary"
          shape="round"
          loading={patchDiscussionWallCommentReplyLoading}
          onClick={() =>
            patchReply({
              variables: {
                input: { replyId: currentThread.id, text: currentThread.text },
              },
            })
          }
        >
          {t('submit')}
        </Button>
      </div>
    ) : (
      <p>{reply.text}</p>
    );
  };

  const renderActionsComment = (comment: CommentType) => {
    if (hideActions) return [];
    return [
      <div
        className="cursor-pointer text-xs mr-2"
        onClick={() => {
          meData &&
            likeComment({
              variables: { id: comment.id },
            });
        }}
      >
        <Tooltip
          key="comment-basic-like"
          title={comment.isLiked ? t('unlike') : t('like')}
        >
          <span className={`${comment.isLiked && 'text-primary-600'}`}>
            {comment.isLiked ? <LikeFilled /> : <LikeOutlined />}
          </span>
          <span
            className={`comment-action ml-1 ${
              comment.isLiked && 'text-primary-600'
            }`}
          >
            {comment.likeCount}
          </span>
        </Tooltip>
      </div>,
      <span
        key="comment-list-reply-to-0"
        data-test="comment-section-reply"
        onClick={() => {
          meData &&
            setCurrentThread({
              id: comment.id,
              text: comment.text,
              author: comment.user,
            });
        }}
      >
        {t('reply')}
      </span>,
    ];
  };

  const renderActionsReply = (reply: Reply) => {
    if (hideActions) return [];
    return [
      <div
        className="cursor-pointer text-xs mr-2"
        onClick={() =>
          meData &&
          likeReply({
            variables: { id: reply.id },
          })
        }
      >
        <Tooltip
          key="comment-basic-like"
          title={reply.isLiked ? t('unlike') : t('like')}
        >
          <span className={`${reply.isLiked && 'text-primary-600'}`}>
            {reply.isLiked ? <LikeFilled /> : <LikeOutlined />}
          </span>
          <span
            className={`comment-action ml-1 ${
              reply.isLiked && 'text-primary-600'
            }`}
          >
            {reply.likeCount}
          </span>
        </Tooltip>
      </div>,
    ];
  };

  return (
    <AntdComment
      className="!bg-transparent"
      actions={renderActionsComment(comment)}
      author={renderAuthor(comment)}
      avatar={<UserAvatar user={comment.user} />}
      content={renderContentComment(comment)}
    >
      {showReplies &&
        comment.replies?.length &&
        comment.replies.map((reply) => {
          return (
            <AntdComment
              key={reply.id}
              actions={renderActionsReply(reply)}
              author={renderAuthor(reply)}
              avatar={<UserAvatar user={reply.user} />}
              content={renderContentReply(reply)}
            />
          );
        })}
    </AntdComment>
  );
}

export default Comment;
